import { deleteData, getData, postData, putData } from "../api/api";

export type SemanticModelStatus = "DRAFT" | "APPROVED";

export interface SemanticTable {
  name: string;
  table: string;
  timeColumn?: string;
  description?: string;
}

export interface SemanticMetric {
  name: string;
  label?: string;
  description?: string;
  table: string;
  expression: string;
  filter?: string;
  timeColumn?: string;
  synonyms?: string[];
}

export interface SemanticDimension {
  name: string;
  label?: string;
  description?: string;
  table: string;
  expression: string;
  type?: "STRING" | "NUMBER" | "TIME" | "BOOLEAN";
  synonyms?: string[];
}

export interface SemanticJoin {
  from: string;
  to: string;
  on: string;
  type?: "INNER" | "LEFT";
}

export interface SemanticRollup {
  prepJobId?: string;
  index: string;
  grain: string;
  metrics: string[];
  dimensions?: string[];
}

export interface SemanticModel {
  id?: string;
  adaptorId: string;
  adaptorName?: string;
  name?: string;
  description?: string;
  status?: SemanticModelStatus;
  tables: SemanticTable[];
  metrics: SemanticMetric[];
  dimensions: SemanticDimension[];
  joins: SemanticJoin[];
  rollups?: SemanticRollup[];
  source?: string;
  approvedBy?: string;
  approvedAt?: string;
}

export const SEMANTIC_GRAINS = ["hour", "day", "week", "month", "quarter", "year"] as const;

export interface SemanticQuery {
  metrics?: string[];
  dimensions?: string[];
  filters?: { field: string; op: string; value?: unknown; values?: unknown[] }[];
  timeRange?: { last?: string; from?: string; to?: string; completeOnly?: boolean };
  grain?: string;
  orderBy?: { field: string; desc?: boolean }[];
  limit?: number;
}

export interface SemanticPreview {
  sql: string;
  columns: string[];
  rows: Record<string, unknown>[];
  truncated?: boolean;
}

export interface SemanticRollupRequest {
  grain: string;
  metrics: string[];
  dimensions?: string[];
  lookback?: string;
  scheduleCron?: string;
  index?: string;
}

const BASE = "/semantic-models";

export const emptySemanticModel = (adaptorId: string): SemanticModel => ({
  adaptorId,
  tables: [],
  metrics: [],
  dimensions: [],
  joins: [],
  rollups: [],
});

export const semanticModelService = {
  list: () => getData(BASE) as Promise<SemanticModel[]>,
  byAdaptor: async (adaptorId: string): Promise<SemanticModel | null> => {
    const res = (await getData(`${BASE}/by-adaptor/${adaptorId}`)) as SemanticModel | "" | null;
    return res && typeof res === "object" ? res : null;
  },
  save: (model: SemanticModel) =>
    (model.id ? putData(`${BASE}/${model.id}`, model) : postData(BASE, model)) as Promise<SemanticModel>,
  approve: (id: string) => postData(`${BASE}/${id}/approve`, {}) as Promise<SemanticModel>,
  remove: (id: string) => deleteData(`${BASE}/${id}`),
  suggest: (adaptorId: string, tables?: string[]) =>
    postData(`${BASE}/suggest`, { adaptorId, tables }) as Promise<SemanticModel>,
  preview: (id: string, query: SemanticQuery) =>
    postData(`${BASE}/${id}/preview`, query) as Promise<SemanticPreview>,
  createRollup: (id: string, body: SemanticRollupRequest) =>
    postData(`${BASE}/${id}/rollups`, body) as Promise<SemanticRollup>,
};
